import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Calendar, DollarSign, Users, FolderTree } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';

const Projects = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    planned_cost: '',
    team_member_ids: [],
    department_id: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
      toast.error('Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        planned_cost: parseFloat(formData.planned_cost),
        department_id: formData.department_id || null
      };
      await axios.post(`${API}/projects`, projectData);
      toast.success('Projekt erfolgreich erstellt!');
      setDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        planned_cost: '',
        team_member_ids: [],
        department_id: ''
      });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Projekts');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-medium">Laden...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="projects-title">
              Projekte
            </h1>
            <p className="text-muted-foreground mt-2">Verwalten Sie alle Ihre Projekte</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-button">
                <Plus className="w-4 h-4 mr-2" />
                Neues Projekt
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-project-dialog">
              <DialogHeader>
                <DialogTitle>Neues Projekt erstellen</DialogTitle>
                <DialogDescription>Fügen Sie ein neues Projekt hinzu</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Projektname</Label>
                  <Input
                    id="name"
                    data-testid="project-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    data-testid="project-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Startdatum</Label>
                    <Input
                      id="start_date"
                      type="date"
                      data-testid="project-start-date-input"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Enddatum</Label>
                    <Input
                      id="end_date"
                      type="date"
                      data-testid="project-end-date-input"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="planned_cost">Geplante Kosten (CHF)</Label>
                  <Input
                    id="planned_cost"
                    type="number"
                    step="0.01"
                    data-testid="project-planned-cost-input"
                    value={formData.planned_cost}
                    onChange={(e) => setFormData({ ...formData, planned_cost: e.target.value })}
                    required
                  />
                </div>
                {departments.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="department">Abteilung</Label>
                    <Select 
                      value={formData.department_id} 
                      onValueChange={(value) => setFormData({ ...formData, department_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="project-department-select">
                        <SelectValue placeholder="Abteilung auswählen (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Keine Abteilung</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            <div className="flex items-center gap-2">
                              <FolderTree className="w-4 h-4" />
                              {dept.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Weisen Sie das Projekt einer Abteilung zu für bessere Sichtbarkeitssteuerung
                    </p>
                  </div>
                )}
                <Button type="submit" className="w-full" data-testid="project-submit-button">
                  Projekt erstellen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {projects.length === 0 ? (
          <Card data-testid="no-projects-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="text-muted-foreground mb-4">
                <Plus className="w-16 h-16" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Keine Projekte vorhanden</h3>
              <p className="text-muted-foreground mb-4">Erstellen Sie Ihr erstes Projekt</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="create-first-project-button">
                <Plus className="w-4 h-4 mr-2" />
                Neues Projekt
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{project.name}</CardTitle>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      project.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {project.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
                    </span>
                  </div>
                  <CardDescription className="line-clamp-2">{project.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(project.start_date).toLocaleDateString('de-CH')} - {new Date(project.end_date).toLocaleDateString('de-CH')}
                  </div>
                  <div className="flex items-center text-sm">
                    <DollarSign className="w-4 h-4 mr-2" />
                    <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      CHF {project.actual_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })} / CHF {project.planned_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {project.actual_cost > project.planned_cost && (
                    <div className="text-xs text-destructive font-medium">
                      ⚠️ Budget überschritten um CHF {(project.actual_cost - project.planned_cost).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="w-4 h-4 mr-2" />
                    {project.owner_name}
                  </div>
                  {project.department_name && (
                    <div className="flex items-center">
                      <Badge variant="outline" className="text-xs">
                        <FolderTree className="w-3 h-3 mr-1" />
                        {project.department_name}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Projects;
